import React, { FC, useEffect, useMemo, useState } from 'react';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';

import ContentBlock from 'components/ContentBlock/Index';
import { ISkillsTab } from 'core/interface/skills';
import { IMe } from 'core/interface/me';
import { SkillService } from 'services/skills/skillService';
import Popup from 'components/shared/Popups/Popup/Index';
import { LanguageService } from 'services/language';
import CandidateSkillChips from 'components/shared/CandidateSkills/CandidateSkillChips';
import CandidateSkillsForm from 'components/shared/CandidateSkills/CandidateSkillsForm';
import { normalizeSkillList } from 'utils/normalizeSkillList';

import './Index.scss';
import 'components/shared/CandidateSkills/candidate-skills.scss';

const SkillsTab: FC<ISkillsTab> = ({
  t,
  candidate_id,
  fromGuest
}): JSX.Element => {
  const me = useSelector((state: IMe) => state.user).user;
  const [showEditSkills, setShowEditSkills] = useState<boolean>(false);
  const { candidates } = useSelector((state: RootState) => state.candidates);

  const hardSkills = useMemo(
    () => normalizeSkillList(candidates?.hard_skills),
    [candidates?.hard_skills]
  );
  const softSkills = useMemo(
    () => normalizeSkillList(candidates?.soft_skills),
    [candidates?.soft_skills]
  );

  useEffect(() => {
    getSkills();
  }, []);

  useEffect(() => {
    getLanguages();
  }, [me]);

  const handleClose = (): void => {
    setShowEditSkills(false);
  };

  const getSkills = async (): Promise<void> => {
    if (candidate_id) {
      await SkillService.getITSkills(candidate_id);
    }
  };

  const getLanguages = async (): Promise<void> => {
    await LanguageService.getCandidateLanguage(me.candidate_id);
  };

  const canEdit = !fromGuest;

  return (
    <div className="skills-block-side cv-skills-review cv-skills-review--profile">
      <ContentBlock
        className="skill-block"
        title={t('Hard Skills')}
        noData={hardSkills.length === 0}
        onActionClick={() => setShowEditSkills(true)}
        showButton={canEdit}
      >
        <div className="cv-skills-review__section">
          <CandidateSkillChips skills={hardSkills} />
        </div>
      </ContentBlock>
      <ContentBlock
        className="skill-block soft-skills"
        title={t('Soft Skills')}
        noData={softSkills.length === 0}
        onActionClick={() => setShowEditSkills(true)}
        showButton={canEdit}
      >
        <div className="cv-skills-review__section">
          <CandidateSkillChips skills={softSkills} />
        </div>
      </ContentBlock>

      {showEditSkills && (
        <Popup
          title={t('Skills')}
          className="expertise cv-skills-review-popup"
          onClose={handleClose}
        >
          <CandidateSkillsForm
            t={t}
            candidateId={candidate_id}
            initialHardSkills={hardSkills}
            initialSoftSkills={softSkills}
            existingHardSkills={hardSkills}
            existingSoftSkills={softSkills}
            showLegend
            legendVariant="profile"
            onClose={handleClose}
          />
        </Popup>
      )}
    </div>
  );
};

export default SkillsTab;
